import { createReducer } from 'redux-act';
import * as action from '../actions/eligibility.actions';
import { updateDynamicState, updateDynamicStateExcept } from './helpers/dynamicReducer.helper';
import eligibilityStateSchema from '../../schemas/eligibilityState.schema';
import { stringifyErrorDetails } from '../../helpers/errorHelper';

const updateEligibilities = updateDynamicState('eligibilities', () => eligibilityStateSchema.parse({}));
const updateEligibilitiesExcept = updateDynamicStateExcept('eligibilities', () => eligibilityStateSchema.parse({}));

const eligibilityErrorState = payload => ({
  errorMessage: typeof payload === 'string' ? payload : payload?.message || 'Eligibility check failed',
  errorDetails: stringifyErrorDetails(payload),
});

const initialAuditLogState = {
  entries: [],
  isLoading: false,
  error: null,
  status: null,
};

const initialAgentView = {
  isOpen: false,
  isLoading: false,
  verificationId: null,
  changes: [],
  issues: [],
  coverages: [],
  summary: '',
  changesAccepted: false,
  changesRejected: false,
  changesStatus: null,
  isBillerVerified: false,
  verifiedAt: null,
  pendingReview: false,
  auditLog: { ...initialAuditLogState },
};

const initialAgentStatus = {
  state: null,
  error: null,
  changesApplied: false,
  isCurrentUserUpdate: false,
};

export const initialState = {
  eligibilities: {},
  html: '',
  portalInfo: '',
  infoExpanded: false,
  errorMessage: '',
  errorDetails: '',
  changesModal: {
    isOpen: false,
    status: null,
    appliedRules: [],
    verificationId: null,
    changes: [],
    issues: [],
    isLoading: false,
    coordinationTypeId: null,
    pendingReview: false,
  },
  agentView: { ...initialAgentView },
  isAcceptingEligibility: false,
  isAgentProcessing: false,
  agentStatus: { ...initialAgentStatus },
  selfInitiatedRun: null,
  runStatus: { requestId: null, status: 'idle', hasRun: false },
};

const eligibilityReducer = createReducer(
  {
    [action.setInfo]: (state, { coordinationTypeId, ...payload }) => ({
      ...state,
      ...updateEligibilities(state, coordinationTypeId, payload),
    }),
    [action.setCreating]: (state, { coordinationTypeId, value, sessionId }) => ({
      ...state,
      ...updateEligibilitiesExcept(
        state,
        coordinationTypeId,
        {
          isEligibilityCreating: value,
          sessionId,
        },
        {
          sessionId: null,
          isEligibilityCreating: false,
          isEligibilityLoading: false,
          isEligibilityDisabled: true,
        },
      ),
    }),
    [action.setLoading]: (state, { coordinationTypeId, value }) => ({
      ...state,
      ...updateEligibilitiesExcept(
        state,
        coordinationTypeId,
        {
          isEligibilityLoading: value,
        },
        {
          isEligibilityLoading: false,
          isEligibilityCreating: false,
          isEligibilityDisabled: true,
        },
      ),
    }),
    [action.setDisabled]: (state, { coordinationTypeId, value }) => ({
      ...state,
      ...updateEligibilities(state, coordinationTypeId, {
        isEligibilityDisabled: value,
      }),
    }),
    [action.resetButtons]: state => ({
      ...state,
      ...updateEligibilitiesExcept(
        state,
        -1,
        {},
        {
          isEligibilityCreating: false,
          isEligibilityLoading: false,
          isEligibilityDisabled: false,
        },
      ),
    }),
    [action.initEligibility]: (state, coordinationTypeId) => ({
      ...state,
      ...updateEligibilities(state, coordinationTypeId, {}),
    }),
    [action.setHTML]: (state, payload) => ({
      ...state,
      html: payload,
      errorMessage: '',
      errorDetails: '',
    }),
    [action.error]: (state, payload) => ({
      ...state,
      ...eligibilityErrorState(payload),
    }),
    [action.closeErrorModal]: state => ({
      ...state,
      errorMessage: '',
      errorDetails: '',
    }),
    [action.closeFrame]: state => ({
      ...state,
      html: '',
      portalInfo: '',
      infoExpanded: false,
    }),
    [action.setPortalInfo]: (state, payload) => ({
      ...state,
      portalInfo: payload,
    }),
    [action.expandPortalInfo]: (state, payload) => ({
      ...state,
      infoExpanded: payload,
    }),
    [action.showChangesModal]: (state, payload) => ({
      ...state,
      changesModal: {
        ...state.changesModal,
        isOpen: true,
        status: payload.status || null,
        appliedRules: payload.appliedRules || [],
        verificationId: payload.verificationId || null,
        changes: payload.changes || [],
        issues: payload.issues || [],
        coordinationTypeId: payload.coordinationTypeId,
        isLoading: false,
        pendingReview: !!payload.pendingReview,
      },
    }),
    [action.hideChangesModal]: state => ({
      ...state,
      changesModal: {
        ...state.changesModal,
        isOpen: false,
        status: null,
        appliedRules: [],
        verificationId: null,
        changes: [],
        issues: [],
        coordinationTypeId: null,
        isLoading: false,
      },
    }),
    [action.acceptChanges]: state => ({
      ...state,
      changesModal: {
        ...state.changesModal,
        isLoading: true,
      },
    }),
    [action.acceptChangesSuccess]: state => ({
      ...state,
      changesModal: {
        ...initialState.changesModal,
      },
    }),
    [action.acceptChangesError]: (state, payload) => ({
      ...state,
      changesModal: {
        ...state.changesModal,
        isLoading: false,
        pendingReview: false,
      },
      errorMessage: payload,
      errorDetails: '',
    }),
    [action.rejectChanges]: state => ({
      ...state,
      changesModal: {
        ...state.changesModal,
        isLoading: true,
      },
    }),
    [action.rejectChangesSuccess]: state => ({
      ...state,
      changesModal: {
        ...initialState.changesModal,
      },
    }),
    [action.rejectChangesError]: (state, payload) => ({
      ...state,
      changesModal: {
        ...state.changesModal,
        isLoading: false,
        pendingReview: false,
      },
      errorMessage: payload,
      errorDetails: '',
    }),
    [action.setAcceptingEligibility]: (state, payload) => ({
      ...state,
      isAcceptingEligibility: payload,
    }),
    [action.setAgentProcessing]: (state, payload) => ({
      ...state,
      isAgentProcessing: payload,
    }),
    [action.fetchRunStatus]: (state, requestId) => ({
      ...state,
      runStatus: { requestId, status: 'loading', hasRun: false },
    }),
    [action.fetchRunStatusSuccess]: (state, { requestId, hasRun }) => ({
      ...state,
      runStatus:
        state.runStatus.requestId === requestId
          ? { requestId, status: 'success', hasRun }
          : state.runStatus,
    }),
    [action.fetchRunStatusFailure]: (state, { requestId }) => ({
      ...state,
      runStatus:
        state.runStatus.requestId === requestId
          ? { requestId, status: 'failure', hasRun: false }
          : state.runStatus,
    }),
    [action.showEligibilityView]: (state, payload) => ({
      ...state,
      agentView: {
        ...initialAgentView,
        isOpen: true,
        verificationId: payload.verificationId || null,
        changes: payload.changes || [],
        issues: payload.issues || [],
        coverages: payload.coverages || [],
        summary: payload.summary || '',
        changesStatus: payload.changesStatus || null,
        isBillerVerified: payload.isBillerVerified || false,
        verifiedAt: payload.verifiedAt || null,
        pendingReview: !!payload.pendingReview,
      },
    }),
    [action.hideEligibilityView]: state => ({
      ...state,
      agentView: { ...initialAgentView },
    }),
    [action.acceptMultiChanges]: state => ({
      ...state,
      agentView: {
        ...state.agentView,
        isLoading: true,
      },
    }),
    [action.acceptMultiChangesSuccess]: state => ({
      ...state,
      agentView: {
        ...state.agentView,
        isLoading: false,
        changesAccepted: true,
        pendingReview: false,
      },
    }),
    [action.acceptMultiChangesError]: (state, payload) => ({
      ...state,
      agentView: {
        ...state.agentView,
        isLoading: false,
        pendingReview: false,
      },
      errorMessage: payload,
      errorDetails: '',
    }),
    [action.rejectMultiChanges]: state => ({
      ...state,
      agentView: {
        ...state.agentView,
        isLoading: true,
      },
    }),
    [action.rejectMultiChangesSuccess]: state => ({
      ...state,
      agentView: {
        ...state.agentView,
        isLoading: false,
        changesRejected: true,
        pendingReview: false,
      },
    }),
    [action.rejectMultiChangesError]: (state, payload) => ({
      ...state,
      agentView: {
        ...state.agentView,
        isLoading: false,
        pendingReview: false,
      },
      errorMessage: payload,
      errorDetails: '',
    }),
    [action.fetchAuditEntries]: state => ({
      ...state,
      agentView: {
        ...state.agentView,
        auditLog: {
          ...state.agentView.auditLog,
          isLoading: true,
          error: null,
        },
      },
    }),
    [action.fetchAuditEntriesSuccess]: (state, payload) => ({
      ...state,
      agentView: {
        ...state.agentView,
        auditLog: {
          ...state.agentView.auditLog,
          isLoading: false,
          entries: payload,
          status: 'success',
        },
      },
    }),
    [action.fetchAuditEntriesFailure]: (state, payload) => ({
      ...state,
      agentView: {
        ...state.agentView,
        auditLog: {
          ...state.agentView.auditLog,
          isLoading: false,
          error: payload,
          status: 'failure',
        },
      },
    }),
    [action.resetAuditEntries]: state => ({
      ...state,
      agentView: {
        ...state.agentView,
        auditLog: { ...initialAuditLogState },
      },
    }),
    [action.setAgentStatus]: (state, payload) => ({
      ...state,
      agentStatus: {
        state: payload.state,
        error: payload.error || null,
        changesApplied: payload.changesApplied || false,
        isCurrentUserUpdate: payload.isCurrentUserUpdate || false,
      },
    }),
    [action.clearAgentStatus]: state => ({
      ...state,
      agentStatus: { ...initialAgentStatus },
    }),
    [action.markSelfInitiatedAgentRun]: (state, payload) => ({
      ...state,
      selfInitiatedRun: payload,
    }),
    [action.checkMultiEligibility]: state => ({
      ...state,
      agentStatus: { ...initialAgentStatus },
    }),
  },
  initialState,
);

export default eligibilityReducer;
